import { Helmet } from "react-helmet-async";
import { Camera, MessageSquare, Paintbrush, Zap } from "lucide-react";
import { siteImages } from "@/lib/siteData";
import TestimonialCard from "@/components/TestimonialCard";
import ServiceContactForm from "@/components/ServiceContactForm";

const steps = [
  {
    step: "First Step",
    title: "Consult",
    icon: MessageSquare,
    description:
      "Before we start any photo shoot or project we have to consult with clients to ensure that we understand exactly what it is they look to accomplish with their shoot. We ask basic info about wardrobe, ideas, the do and don'ts, cans and can'ts!",
  },
  {
    step: "Second Step",
    title: "Creative Briefing",
    icon: Paintbrush,
    description:
      "Our favorite part of the photo shoot process by far. Here we get to map out all of the creative details concerning the photo shoot as well as add other creative ideas that may enhance the final product or in this case, the final picture.",
  },
  {
    step: "Third Step",
    title: "Action! Time to Shoot!",
    icon: Zap,
    description:
      "Time to get down to the business of the photo shoot. Now Pose! This is where all of the things discussed in the consultation and creative planning phase is brought to life. When we shoot! We don't miss! Mission accomplished!",
  },
];

const photographers = [
  {
    name: "Cedric Searcy",
    image: "/__l5e/assets-v1/73d82765-14f5-40bb-a355-19157676f2fd/Personnel-Cedric-700x853.jpg",
    slug: "cedric-searcy",
  },
  {
    name: "Tabitha Suggs-Searcy",
    image: "/__l5e/assets-v1/85981944-86fd-4313-9c08-da9c6b1d30c4/tab-editorial-1-700x935.jpg",
    slug: "tabitha-suggs-searcy",
  },
  {
    name: "The Pentaxx Guy",
    image: "/__l5e/assets-v1/7f7074a1-3580-4788-b7b5-c7d6426cdda9/pentaxx-guy-102-700x935.jpg",
    slug: "the-pentaxx-guy",
  },
  {
    name: "Gary FRLN Jackson",
    image: "/__l5e/assets-v1/9bb34835-244e-4dc0-ba3e-e56be3733636/gary-700x853.jpg",
    slug: "gary-jackson",
  },
  {
    name: "Rachael Banks",
    image: "/__l5e/assets-v1/cc320053-17da-47ba-a9b1-f41fc76e913b/rach-banks-web-1-700x853.jpg",
    slug: "rachael-banks",
  },
  {
    name: "Deontae Holloway",
    image: "/__l5e/assets-v1/ddec478d-7ab3-410d-a0d6-b4719a6a29c8/deontae-700x853.jpg",
    slug: "deontae-holloway",
  },
];

const PhotographyServicesPage = () => (
  <div className="pt-20">
    <Helmet>
      <title>Photo Services Columbus GA | Portrait, Brand, Commercial</title>
      <meta name="description" content="Professional photography services in Columbus, GA. Commercial, portrait, fashion, brand, and bridal photography by skilled photographers." />
      <link rel="canonical" href="https://bookmystudiospace.com/columbus-photography-service" />
      <meta property="og:title" content="Photo Services Columbus GA | Portrait, Brand, Commercial" />
      <meta property="og:description" content="Professional photography services in Columbus, GA. Commercial, portrait, fashion, brand, and bridal photography." />
      <meta property="og:url" content="https://bookmystudiospace.com/columbus-photography-service" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`https://bookmystudiospace.com${siteImages.portfolioOutside}`} />
    </Helmet>
    {/* Hero */}
    <section className="py-20 lg:py-28 text-center">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">
          PHOTOGRAPHY SERVICES
        </h1>
        <p className="text-muted-foreground text-lg font-body max-w-2xl mx-auto">
          COMMERCIAL – PORTRAIT – FASHION – BRAND – BRIDAL &amp; MORE!
        </p>
      </div>
    </section>

    {/* Description */}
    <section className="pb-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-panel border border-border p-8 text-center">
          <Camera className="w-12 h-12 text-primary mx-auto mb-6" />
          <p className="text-foreground/80 font-body leading-relaxed">
            Our expertise extends beyond one genre of photography and that's
            exactly why we are considered full photography service providers in
            our area. Whether your needs are small or large scale, our network of
            skilled photographers are prepared to assist you with any project you
            need assistance with. We provide commercial photography, brand &amp;
            product photography, fashion &amp; editorial photography, wedding
            &amp; bridal photography, maternity photography, and other genres as
            well.
          </p>
        </div>
      </div>
    </section>

    {/* 3-Step Process */}
    <section className="pb-20">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div
              key={s.title}
              className="bg-panel border border-border p-8 text-center"
            >
              <s.icon className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-xs text-muted-foreground font-body uppercase tracking-widest mb-1">
                {s.step}
              </p>
              <h2 className="text-xl font-display tracking-wider mb-4">
                {s.title}
              </h2>
              <p className="text-sm text-foreground/80 font-body leading-relaxed">
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Meet Photographers */}
    <section className="pb-20">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-4xl font-display tracking-widest text-center mb-4">
          MEET PHOTOGRAPHERS
        </h2>
        <p className="text-muted-foreground font-body text-center mb-12">
          Some of the people that shoot here
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
          {photographers.map((p) => (
            <div key={p.slug} className="group text-center">
              <div className="aspect-[3/4] overflow-hidden border border-border mb-3">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <p className="text-sm font-display tracking-wider">{p.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonial */}
    <TestimonialCard
      quote="Cedric is definitely the guy to go to for capturing those special moments! Highly recommended! Cedric is awesome in what he does and works diligently to make sure everything goes smooth and perfect. I was honored to have him do my daughter's birthday and graduation shoot, exceeding my expectations."
      name="Angie Perez"
      title="Jade's Dope Mom"
      image="/__l5e/assets-v1/85981944-86fd-4313-9c08-da9c6b1d30c4/tab-editorial-1-700x935.jpg"
    />

    {/* Contact Form */}
    <ServiceContactForm defaultService="Photography" />
  </div>
);

export default PhotographyServicesPage;
